/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IRequestContext } from '@msdyn365-commerce/core';
import { debounce } from 'lodash';
import { observer } from 'mobx-react';
import * as React from 'react';

import { isMobile, VariantType } from '@msdyn365-commerce-modules/utilities';

import { IIncrementalQuantityProps } from './IncrementalQuantity.props';

/**
 * Extend the IIncrementalQuantityProps interface to include productMultiple and setRoundedUp
 */
export interface IIncrementalQuantityPropsExtended extends IIncrementalQuantityProps {
    productMultiple?: number;
    setRoundedUp?: (value: boolean) => void;
    isNomenclature?: boolean;
}

/**
 * IIncrementalQuantityState interface.
 */
interface IIncrementalQuantityState {
    currentInput: number;
    isUpdating: boolean;
}

/**
 * Quantity Component - This component is used to add or remove quantity.
 */
@observer
export default class IncrementalQuantity extends React.PureComponent<IIncrementalQuantityPropsExtended, IIncrementalQuantityState> {
    public static defaultProps: Partial<IIncrementalQuantityPropsExtended> = {
        min: 1,
        decrementGlyphClass: 'fas fa-minus',
        incrementGlyphClass: 'fas fa-plus',
        locale: 'fr-CA',
        productMultiple: 0,
        setRoundedUp: undefined
    };

    private _minValue: number;
    private EPSILON = 1e-10;

    private readonly inputRef: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    public static getDerivedStateFromProps(
        props: Readonly<IIncrementalQuantityPropsExtended>,
        state: IIncrementalQuantityState
    ): IIncrementalQuantityState {
        const nextState = { ...state };

        if (props.disabled && !state.isUpdating) {
            nextState.isUpdating = true;
        }

        if (
            (!props.disabled && state.isUpdating) ||
            ((props.applyDefaultOrderSettings ?? false) &&
                state.currentInput === 1 &&
                props.currentCount &&
                props.currentCount > IncrementalQuantity.defaultProps.min!)
        ) {
            nextState.currentInput = props.currentCount ?? 1;
            nextState.isUpdating = false;
        }

        return nextState;
    }

    constructor(props: IIncrementalQuantityPropsExtended) {
        super(props);
        this.state = {
            currentInput: props.currentCount ?? 1,
            isUpdating: false
        };
        this._onIncrement = this._onIncrement.bind(this);
        this._onDecrement = this._onDecrement.bind(this);
        this._handleChange = this._handleChange.bind(this);
        this._minValue = this.props.min ?? IncrementalQuantity.defaultProps.min!;
    }

    public componentDidMount(): void {
        if (window) {
            this.initialize();
        }
    }

    public render(): JSX.Element {
        const { min, max } = this.props;

        this._minValue = min ?? IncrementalQuantity.defaultProps.min!;
        const value = this.state.currentInput;

        const glyphMinusClassName: string = `${this.props.decrementGlyphClass!} quantity__controls-glyph`;
        const glyphPlusClassName: string = `${this.props.incrementGlyphClass!} quantity__controls-glyph`;
        const decrementDisabled = value <= this._minValue || this.props.isGiftCard;
        const incrementDisabled = value >= max || this.props.isGiftCard;
        const currentValue = value;
        const ariaLablelText = `${this.props.inputQuantityAriaLabel ?? ''} ${value}`;

        let extraClassDecrement = '';
        if (decrementDisabled) {
            // The quantity has reached its boundaries (max or min)
            extraClassDecrement = 'disabled';
        } else if (this.props.disabled) {
            // This.props.disabled shows if the state is not succeded yet
            extraClassDecrement = 'transition';
        }
        let extraClassIncrement = '';
        if (incrementDisabled) {
            // The quantity has reached its boundaries (max or min)
            extraClassIncrement = 'disabled';
        } else if (this.props.disabled) {
            // This.props.disabled shows if the state is not succeded yet
            extraClassIncrement = 'transition';
        }

        return (
            <>
                <div className='quantity' id={this.props.id}>
                    {!this.props.isNomenclature && (
                        <button
                            disabled={this.props.disabled || decrementDisabled}
                            title={decrementDisabled ? '' : this.props.decrementButtonAriaLabel}
                            className={`decrement quantity__controls ${extraClassDecrement}`}
                            onClick={this._onDecrement}
                            aria-hidden={!!decrementDisabled}
                            aria-label={`${this.props.decrementButtonAriaLabel}`}
                            color='secondary'
                        >
                            <span className={glyphMinusClassName} />
                        </button>
                    )}
                    <input
                        id='floatTextBox'
                        className='quantity-input'
                        onChange={this._handleChange}
                        aria-live='polite'
                        aria-label={this.isMobileView() ? ariaLablelText : `${this.props.inputQuantityAriaLabel ?? ''}`}
                        role='spinbutton'
                        aria-valuemin={this._minValue}
                        aria-valuemax={max}
                        aria-valuenow={currentValue}
                        ref={this.inputRef}
                        disabled={this.props.disabled}
                        min={this._minValue}
                        max={max}
                        defaultValue={this.parseToLocaleText(this.state.currentInput)}
                    />
                    {!this.props.isNomenclature && (
                        <button
                            disabled={this.props.disabled || incrementDisabled}
                            title={incrementDisabled ? '' : this.props.incrementButtonAriaLabel}
                            className={`increment quantity__controls ${extraClassIncrement}`}
                            onClick={this._onIncrement}
                            aria-hidden={!!incrementDisabled}
                            aria-label={`${this.props.incrementButtonAriaLabel}`}
                            color='secondary'
                        >
                            <span className={glyphPlusClassName} />
                        </button>
                    )}
                </div>
            </>
        );
    }

    /**
     * It checks if its in mobile view or not.
     * @returns Boolean.
     */
    private readonly isMobileView = (): boolean => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Added gridsetting values.
        const context = {
            gridSettings: {
                xs: { w: 767 },
                sm: { w: 991 },
                md: { w: 1199 },
                lg: { w: 1599 },
                xl: { w: 1600 }
            }
        } as IRequestContext;

        const isMobileCheck = isMobile({ variant: VariantType.Browser, context });
        return isMobileCheck === 'xs' || isMobileCheck === 'sm';
    };

    private _onIncrement(): void {
        let invokeCallback = false;
        const currQuantity = this.state.currentInput;
        let updatedQuantity: number;

        if (currQuantity < this.props.max) {
            invokeCallback = true;
            updatedQuantity = this.roundUpToMultiple(currQuantity + 1);
        } else {
            invokeCallback = false;
            updatedQuantity = this.props.max;
        }

        if (invokeCallback && this.props.onChange) {
            if (this.props.onChange(updatedQuantity)) {
                this.setState({ currentInput: updatedQuantity });
                this.onInputChange(updatedQuantity);
            }
        }
    }

    private _onDecrement(): void {
        let invokeCallback = false;
        const currQuantity = this.state.currentInput;
        let updatedQuantity: number;

        if (currQuantity >= this._minValue) {
            invokeCallback = true;
            updatedQuantity = this.multipleOnDecrement(currQuantity - 1);
        } else {
            invokeCallback = false;
            updatedQuantity = this._minValue;
        }

        if (invokeCallback && this.props.onChange) {
            if (this.props.onChange(updatedQuantity)) {
                this.setState({ currentInput: updatedQuantity });
                this.onInputChange(updatedQuantity);
            }
        }
    }

    private _debouncedOnChange = debounce(() => {
        this.props.onChange && this.props.onChange(this.state.currentInput);

        const inputElement = this.inputRef?.current;
        if (inputElement instanceof HTMLInputElement) {
            inputElement.setAttribute('aria-valuenow', this.state.currentInput.toString());
            inputElement.setAttribute('value', this.state.currentInput.toString());
        }
    }, 2000);

    private _debouncedValidate = debounce(() => {
        this._validateMinAndRound();
    }, 1000);

    private _handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
        if (this.inputRef.current) {
            this.inputRef.current.value = this.parseToLocaleText(this.inputRef.current.value);
        }

        let currentValue = parseFloat(e.target.value.replace(',', '.'));

        if (Number.isNaN(currentValue)) {
            this.setState({ currentInput: currentValue });
            return;
        }

        currentValue = Math.min(this.props.max, Math.max(this._minValue, currentValue));
        this.setState({ currentInput: currentValue });

        this._debouncedOnChange();
        this._debouncedValidate();
    }

    /**
     * Validates min and updates its value.
     */
    private readonly _validateMinAndRound = (): void => {
        if (Number.isNaN(this.state.currentInput)) {
            this.setState({ currentInput: this._minValue });
        } else {
            let currentValue = Math.min(this.props.max, Math.max(this._minValue, this.state.currentInput));
            currentValue = this.roundUpToMultiple(currentValue);

            if (currentValue !== this.state.currentInput) {
                this.setState({ currentInput: currentValue });
            }

            if (this.props.onChange) {
                if (this.props.onChange(currentValue)) {
                    this.onInputChange(currentValue);
                }
            }
        }
    };

    private setInputFilter(textbox: Element, inputFilter: (value: string) => boolean, errMsg: string): void {
        if (!textbox) {
            console.error('Textbox element is null');
            return; // Exit the function if the textbox is null
        }

        ['input', 'keydown', 'keyup', 'mousedown', 'mouseup', 'select', 'contextmenu', 'drop', 'focusout'].forEach(function(event) {
            textbox.addEventListener(event, function(
                this: (HTMLInputElement | HTMLTextAreaElement) & {
                    oldValue: string;
                    oldSelectionStart: number | null;
                    oldSelectionEnd: number | null;
                }
            ) {
                if (inputFilter(this.value)) {
                    this.oldValue = this.value;
                    this.oldSelectionStart = this.selectionStart;
                    this.oldSelectionEnd = this.selectionEnd;
                } else if (Object.prototype.hasOwnProperty.call(this, 'oldValue')) {
                    this.value = this.oldValue;

                    if (this.oldSelectionStart !== null && this.oldSelectionEnd !== null) {
                        this.setSelectionRange(this.oldSelectionStart, this.oldSelectionEnd);
                    }
                } else {
                    this.value = '';
                }
            });
        });
    }

    private initialize(): void {
        // Call the setInputFilter for the specific input box
        this.setInputFilter(
            //@ts-ignore
            document.getElementById('floatTextBox'), // ID of the input element
            function(value: string) {
                return /^\d*[.,]?\d{0,2}$/.test(value); // Regex for floating-point numbers
            },
            'Must be a floating (real) number'
        );
    }

    private parseToLocaleText(currentInput: string | number): string {
        return this.props.locale === 'fr-CA' ? currentInput.toString().replace('.', ',') : currentInput.toString();
    }

    private onInputChange(value: number): void {
        if (this.inputRef.current) {
            this.inputRef.current.value = this.parseToLocaleText(value);
        }
    }

    private roundUpToMultiple(value: number): number {
        const localProductMultiple = this.props.productMultiple;

        if (localProductMultiple && localProductMultiple > 0 && this.props.setRoundedUp) {
            if (localProductMultiple !== 0 && Math.abs(value % localProductMultiple) > this.EPSILON) {
                const roundedValue = Math.ceil(value / localProductMultiple) * localProductMultiple;

                if (roundedValue < this.props.max && roundedValue > this._minValue) {
                    this.props.setRoundedUp(true);
                }

                return parseFloat(roundedValue.toFixed(2));
            }
        }

        if (this.props.setRoundedUp) {
            this.props.setRoundedUp(false);
        }

        return value;
    }

    private multipleOnDecrement(value: number): number {
        const localProductMultiple = this.props.productMultiple || 0;
        const rounded = parseFloat((value - (value % localProductMultiple)).toFixed(2));

        if (localProductMultiple === 0) {
            return value;
        }

        if (isNaN(rounded) || (!isNaN(rounded) && rounded <= 0)) {
            return this._minValue;
        }

        return rounded;
    }
}
